// ✅ src/pages/Sales/LeadSalesMerge.jsx
// UPDATED: localStorage headers added in all API calls

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaArrowLeft,
  FaPlus,
  FaEye,
  FaSave,
  FaFileUpload,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const LeadSalesMerge = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const location = useLocation();
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId],
  );

  // ✅ salesLeadId passed from listing page
  const salesLeadId = location.state?.salesLeadId || null;
  const isEdit = Boolean(salesLeadId);

  // dropdowns
  const [enquiryNumbers, setEnquiryNumbers] = useState([]);
  const [customerNames, setCustomerNames] = useState([]);
  const [assetCodes, setAssetCodes] = useState([]);

  const [loadingDD, setLoadingDD] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  // Search selection
  const [searchPick, setSearchPick] = useState({
    enquiryNumber: "",
    customerName: "",
  });

  // Full form
  const [form, setForm] = useState({
    idvalue: "",
    enquiryNumber: "",
    customerName: "",
    leadSource: "",
    productName: "",
    salesPerson: "",
    address1: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    mobilenum: "",
    dob: "",

    assetId: "",
    assetcodeplot: "",
    assetClass: "",
    customerId: "",
    location: "",
    addressSales: "",
    partyname: "",
    pattaNo: "",
    panchayathNo: "",
    surveyNo: "",
    sellerName: "",
    extend: "",
    dateOfBooking: "",
    purchaseValue: "",
    paymentMode: "",
  });

  const paymentModes = ["Full Payment", "Part Payment", "EMI"];

  // Documents staged
  const [pickedFiles, setPickedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Documents returned after save
  const [savedDocs, setSavedDocs] = useState([]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // ✅ if edit mode -> load existing
  useEffect(() => {
    if (salesLeadId) {
      loadSalesLeadForEdit(salesLeadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesLeadId]);

  // -------------------------
  // Load dropdowns
  // -------------------------
  const loadDropdowns = async () => {
    try {
      setLoadingDD(true);

      const [enqRes, cusRes, assetRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/leadEntry/enquiries`, {
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/leadEntry/customers`, {
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/leadEntry/assetcodes`, {
          headers: commonHeaders,
        }),
      ]);

      setEnquiryNumbers(enqRes.data?.data || []);
      setCustomerNames(cusRes.data?.data || []);
      setAssetCodes(assetRes.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdowns");
    } finally {
      setLoadingDD(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
  }, [commonHeaders]);

  const loadSalesLeadForEdit = async (id) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/leadEntry/getById`, {
        params: { salesLeadId: id },
        headers: commonHeaders,
      });

      const row = res.data?.data;
      if (!row) {
        toast.error("Record not found");
        return;
      }

      setForm({
        idvalue: row.fld_salescusid || "",
        enquiryNumber: row.fld_enquirnum || "",
        customerName: row.fld_customername || "",
        leadSource: row.fld_leadsource || "",
        productName: row.fld_Productname || row.fld_productname || "",
        salesPerson: row.fld_salesperson || "",

        address1: row.fld_address || "",
        city: row.fld_city || "",
        state: row.fld_state || "",
        country: row.fld_country || "",
        pincode: row.fld_pincode || "",
        mobilenum: row.fld_mobile || "",
        dob: row.fld_dob || "",

        assetId: row.fld_assetid || "",
        assetcodeplot: row.fld_plotassetcode || "",
        assetClass: row.fld_assetclass || "",
        customerId: row.fld_customerid || "",
        location: row.fld_location || "",

        addressSales: row.fld_address_sales || row.fld_address || "",
        partyname: row.fld_propertyname || "",
        pattaNo: row.fld_pattano || "",
        panchayathNo: row.fld_panchayathno || "",
        surveyNo: row.fld_surveyno || "",
        sellerName: row.fld_sellername || "",
        extend: row.fld_extend || "",
        dateOfBooking: row.fld_acquisitiondate || "",
        purchaseValue: row.fld_PurchaseValue || row.fld_purchasevalue || "",
        paymentMode: row.fld_paymentmode || "",
      });

      toast.success("Edit data loaded");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load edit data");
    }
  };

  // -------------------------
  // Search lead details
  // -------------------------
  const handleSearch = async () => {
    try {
      if (!searchPick.enquiryNumber && !searchPick.customerName) {
        toast.warning("Please select Enquiry Number or Customer Name!");
        return;
      }

      setLoadingSearch(true);
      setSavedDocs([]);
      setPickedFiles([]);

      const res = await axios.get(`${BASE_URL}/api/leadEntry/search`, {
        params: {
          enquiryNumber: searchPick.enquiryNumber || "%",
          customerName: searchPick.customerName || "%",
        },
        headers: commonHeaders,
      });

      const row = res.data?.data?.[0];
      if (!row) {
        toast.warning("No records found!");
        return;
      }

      setForm((p) => ({
        ...p,
        idvalue: row.fld_salescusid || row.fld_id || row.fld_customerid || "",
        enquiryNumber: row.fld_enquirnum || "",
        customerName: row.fld_customername || "",
        leadSource: row.fld_leadsource || "",
        productName: row.fld_Productname || row.fld_productname || "",
        salesPerson: row.fld_salesperson || "",

        address1: row.fld_address1 || row.fld_address || "",
        city: row.fld_city || "",
        state: row.fld_state || "",
        country: row.fld_country || "",
        pincode: row.fld_pincode || "",
        mobilenum: row.fld_mobile || row.fld_mobileno || "",
        dob: row.fld_dob || "",

        addressSales: row.fld_address || row.fld_address_sales || "",
        partyname: row.fld_propertyname || "",
        purchaseValue: row.fld_PurchaseValue || row.fld_purchasevalue || "",
      }));

      toast.success("Lead details loaded!");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Search failed");
    } finally {
      setLoadingSearch(false);
    }
  };

  // -------------------------
  // Asset selection -> fetch asset details
  // -------------------------
  const handleAssetSelect = async (assetId) => {
    try {
      setField("assetId", assetId);

      const assetCodeText =
        assetCodes.find((x) => String(x.assetId) === String(assetId))
          ?.assetCode || "";
      setField("assetcodeplot", assetCodeText);

      if (!assetId) return;

      const res = await axios.get(
        `${BASE_URL}/api/leadEntry/asset/${assetId}`,
        {
          headers: commonHeaders,
        },
      );

      const a = res.data?.data;
      if (!a) return;

      setForm((p) => ({
        ...p,
        assetClass: a.fld_assetclass || a.fld_asset_class || p.assetClass || "",
        location: a.fld_location || p.location || "",
        panchayathNo: a.fld_panchayathno || "",
        pattaNo: a.fld_pattano || "",
        surveyNo: a.fld_surveyno || "",
        sellerName: a.fld_sellername || "",
        extend: a.fld_extend || "",

        addressSales: a.fld_address || p.addressSales || "",
        partyname: p.partyname || a.fld_propertyname || "",
        purchaseValue:
          p.purchaseValue || a.fld_PurchaseValue || a.fld_purchasevalue || "",
      }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load asset details");
    }
  };

  // -------------------------
  // Pick files
  // -------------------------
  const handlePickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const okTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    for (const f of files) {
      if (!okTypes.includes(f.type)) {
        toast.error(`Invalid file type: ${f.name}`);
        return;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`File too large (max 5MB): ${f.name}`);
        return;
      }
    }

    setPickedFiles((prev) => [...prev, ...files]);
    toast.success(`${files.length} file(s) selected`);
  };

  const removePicked = (idx) =>
    setPickedFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleViewDocuments = () => {
    if (savedDocs.length > 0) {
      const f = savedDocs[0];
      window.open(
        `${BASE_URL}/Uploadfiles/${encodeURIComponent(f.path)}`,
        "_blank",
      );
      return;
    }
    if (pickedFiles.length === 0) {
      toast.warning("No documents to view!");
      return;
    }
    toast.info("Files are selected. Save to upload and view from server.");
  };

  // -------------------------
  // Save (INSERT or UPDATE)
  // -------------------------
  const handleSave = async () => {
    try {
      if (!form.enquiryNumber || !form.customerName) {
        toast.error("Enquiry Number and Customer Name required!");
        return;
      }
      if (!form.assetId) {
        toast.error("Please select Asset Code!");
        return;
      }
      if (
        !form.customerId ||
        String(form.customerId).trim() === "" ||
        String(form.customerId).trim() === "0"
      ) {
        toast.error(
          "Customer ID is missing. Please search and select a valid lead first!",
        );
        return;
      }
      if (!form.paymentMode) {
        toast.error("Please select Payment Mode!");
        return;
      }

      if (!isEdit && pickedFiles.length === 0) {
        toast.warning("Please add at least one document!");
        return;
      }

      setLoadingSave(true);

      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));

      fd.append("createdBy", String(userId || "0"));
      fd.append("createdDate", new Date().toISOString());
      fd.append("salesLeadId", salesLeadId || "");

      pickedFiles.forEach((f) => fd.append("documents", f));

      const url = isEdit
        ? `${BASE_URL}/api/leadEntry/update`
        : `${BASE_URL}/api/leadEntry/save`;

      const res = isEdit
        ? await axios.put(url, fd, {
            headers: {
              ...commonHeaders,
              "Content-Type": "multipart/form-data",
            },
          })
        : await axios.post(url, fd, {
            headers: {
              ...commonHeaders,
              "Content-Type": "multipart/form-data",
            },
          });

      toast.success(res.data?.message || (isEdit ? "Updated!" : "Saved!"));
      setSavedDocs(res.data?.docs || []);
      setPickedFiles([]);

      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <SalesLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">LEAD SALES MERGE</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <button
          onClick={handleBack}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <FaArrowLeft /> Back
        </button>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1
              className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Lead Sales Merge
            </h1>
          </div>

          <div
            className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Enquiry Number
                </label>
                <select
                  value={searchPick.enquiryNumber}
                  onChange={(e) =>
                    setSearchPick((p) => ({
                      ...p,
                      enquiryNumber: e.target.value,
                    }))
                  }
                  disabled={loadingDD}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {enquiryNumbers.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Customer Name
                </label>
                <select
                  value={searchPick.customerName}
                  onChange={(e) =>
                    setSearchPick((p) => ({
                      ...p,
                      customerName: e.target.value,
                    }))
                  }
                  disabled={loadingDD}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {customerNames.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleSearch}
                disabled={loadingSearch}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all ${buttonClass} ${hoverClass} text-white ${
                  loadingSearch ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSearch /> {loadingSearch ? "Searching..." : "Search"}
              </button>
              <button
                onClick={() =>
                  setSearchPick({
                    enquiryNumber: "",
                    customerName: "",
                  })
                }
                disabled={loadingSearch}
                className={`flex items-center gap-2 px-8 py-3 ml-5 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all ${
                  isDark
                    ? "bg-gray-600 hover:bg-gray-500"
                    : "bg-gray-500 hover:bg-gray-600"
                } text-white`}
              >
                Reset
              </button>
            </div>
          </div>

          <div
            className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-4">
                {[
                  ["Enquiry Number", "enquiryNumber"],
                  ["Customer Name", "customerName"],
                  ["Lead Source", "leadSource"],
                  ["Product Name", "productName"],
                  ["Sales Person", "salesPerson"],
                  ["Address", "address1"],
                  ["City", "city"],
                  ["State", "state"],
                  ["Country", "country"],
                  ["Pin Code", "pincode"],
                  ["Mobile Number", "mobilenum"],
                  ["DOB", "dob"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label
                      className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      {label}
                    </label>
                    <input
                      value={form[key]}
                      onChange={(e) => {
                        if (key === "mobilenum") {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          setField(key, value);
                        } else {
                          setField(key, e.target.value);
                        }
                      }}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Plot No / Asset Code
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.assetId}
                    onChange={(e) => handleAssetSelect(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {assetCodes.map((a) => (
                      <option key={a.assetId} value={a.assetId}>
                        {a.assetCode}
                      </option>
                    ))}
                  </select>
                </div>

                {[
                  ["Asset Class", "assetClass"],
                  ["Customer ID", "customerId"],
                  ["Location", "location"],
                  ["Address", "addressSales"],
                  ["Property In the Name of", "partyname"],
                  ["Patta No", "pattaNo"],
                  ["Panchayath No", "panchayathNo"],
                  ["Survey No", "surveyNo"],
                  ["Seller Name", "sellerName"],
                  ["Extend", "extend"],
                  ["Date of Booking", "dateOfBooking"],
                  ["Value of Asset", "purchaseValue"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label
                      className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      {label}
                    </label>
                    <input
                      value={form[key]}
                      onChange={(e) => setField(key, e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                ))}

                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Payment Mode
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6 items-center mt-1">
                    {paymentModes.map((m) => (
                      <label
                        key={m}
                        className={`flex items-center gap-2 ${
                          isDark ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMode"
                          checked={form.paymentMode === m}
                          onChange={() => setField("paymentMode", m)}
                        />
                        {m}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t mt-8 pt-6">
              <h3
                className={`text-lg font-semibold mb-3 ${isDark ? "text-white" : "text-blue-900"}`}
              >
                Original Document
                <span className="text-red-500">*</span>
              </h3>

              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handlePickFiles}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className={`flex-1 px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:text-white"
                      : "bg-white border-gray-300 text-gray-900 file:bg-gray-100 file:text-gray-700"
                  } file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold`}
                />

                <button
                  onClick={() => {
                    if (!pickedFiles.length)
                      toast.warning("Select file(s) first!");
                    else toast.success("Added to list!");
                  }}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold bg-green-600 hover:bg-green-700 text-white"
                >
                  <FaPlus /> ADD
                </button>
              </div>

              <div className="mt-4">
                {pickedFiles.length > 0 && (
                  <div
                    className={`p-4 rounded-lg border ${
                      isDark
                        ? "bg-gray-700/50 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div
                      className={`font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      Selected Documents ({pickedFiles.length})
                    </div>
                    <div className="space-y-2 max-h-44 overflow-y-auto">
                      {pickedFiles.map((f, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-2 rounded border ${
                            isDark
                              ? "bg-gray-800 border-gray-600"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <FaFileUpload className="text-blue-500" />
                            <div>
                              <div
                                className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                              >
                                {f.name}
                              </div>
                              <div
                                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {(f.size / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removePicked(idx)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {savedDocs.length > 0 && (
                  <div
                    className={`mt-4 p-4 rounded-lg border ${
                      isDark
                        ? "bg-gray-700/50 border-gray-600"
                        : "bg-green-50 border-green-200"
                    }`}
                  >
                    <div
                      className={`font-semibold mb-2 ${isDark ? "text-white" : "text-green-900"}`}
                    >
                      Uploaded Documents
                    </div>
                    <div className="space-y-2">
                      {savedDocs.map((d) => (
                        <button
                          key={d.path}
                          className={`block text-left underline ${
                            isDark ? "text-blue-200" : "text-blue-700"
                          }`}
                          onClick={() =>
                            window.open(
                              `${BASE_URL}/Uploadfiles/${encodeURIComponent(d.path)}`,
                              "_blank",
                            )
                          }
                        >
                          {d.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 pt-6">
                <button
                  onClick={handleViewDocuments}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaEye /> View
                </button>

                <button
                  onClick={handleSave}
                  disabled={loadingSave}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all bg-green-600 hover:bg-green-700 text-white ${
                    loadingSave ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSave /> {loadingSave ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default LeadSalesMerge;
